import { createSlice } from "@reduxjs/toolkit";
import { getPlaces, saveZone, saveMarker, deleteZone, deleteMarker } from "./placesApi";
import { tagList } from "../../utils/placeTagsOptions";

const maxMarkersAutoLoad = 21;
const maxZonesAutoLoad = 21;

// Special value to represent zones with no tags
export const NO_TAG_VALUE = "__NO_TAG__";

const parseNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const sortIdsByName = (ids, getName) => {
    const sorted = [...ids];
    sorted.sort((a, b) => {
        const aName = (getName(a) ?? "").toString();
        const bName = (getName(b) ?? "").toString();
        const nameCompare = aName.localeCompare(bName, undefined, { sensitivity: 'base', numeric: true });
        if (nameCompare !== 0) {
            return nameCompare;
        }
        return `${a}`.localeCompare(`${b}`, undefined, { sensitivity: 'base', numeric: true });
    });
    return sorted;
};

const normalizeMarkerData = (marker) => {
    if (!marker) {
        return null;
    }
    const coords = marker.marker_coords || {};
    const lat = parseNumber(coords.lat);
    const lng = parseNumber(coords.lng);
    if (lat === null || lng === null) {
        return null;
    }
    return {
        ...marker,
        marker_coords: { lat, lng },
    };
};

const normalizeZoneData = (zone) => {
    if (!zone || !Array.isArray(zone.zone_coords)) {
        return null;
    }
    const coords = [];
    for (const point of zone.zone_coords) {
        const lat = parseNumber(point.lat);
        const lng = parseNumber(point.lng);
        if (lat === null || lng === null) {
            return null;
        }
        coords.push({ lat, lng });
    }
    return {
        ...zone,
        zone_coords: coords,
    };
};

const buildZonesBySearch = (state, search) => {
    const searchUpper = search.toUpperCase();
    const list = [...state.pinnedZones];
    state.zonesList.forEach(zoneId => {
        const zone = state.zones[`${zoneId}`];
        if (zone && zone.zone_name && zone.zone_name.toUpperCase().search(searchUpper) !== -1) {
            if (list.indexOf(zoneId) === -1) {
                list.push(zoneId);
            }
        }
    });
    return list;
};

const buildMarkersBySearch = (state, search) => {
    const searchUpper = search.toUpperCase();
    const list = [...state.pinnedMarkers];
    state.markersList.forEach(markerId => {
        const marker = state.markers[`${markerId}`];
        if (marker && marker.marker_name && marker.marker_name.toUpperCase().search(searchUpper) !== -1) {
            if (list.indexOf(markerId) === -1) {
                list.push(markerId);
            }
        }
    });
    return list;
};

const buildZonesBySelectedTag = (state, tag) => {
    const list = [...state.pinnedZones];
    state.zonesList.forEach(zoneId => {
        const zone = state.zones[`${zoneId}`];
        if (zone && zone.zone_tags && zone.zone_tags.indexOf(tag) !== -1) {
            if (list.indexOf(zoneId) === -1) {
                list.push(zoneId);
            }
        }
    });
    return list;
};

const buildMarkersBySelectedTag = (state, tag) => {
    const list = [...state.pinnedMarkers];
    state.markersList.forEach(markerId => {
        const marker = state.markers[`${markerId}`];
        if (marker && marker.marker_tags && marker.marker_tags.indexOf(tag) !== -1) {
            if (list.indexOf(markerId) === -1) {
                list.push(markerId);
            }
        }
    });
    return list;
};

const applyZoneTagFiltersToMap = (state) => {
    if (state.zoneTagFilters && state.zoneTagFilters.length > 0 && state.zoneTagFilters.length < tagList.length + 1) { // +1 for NO_TAG_VALUE
        let filteredZones = [...state.pinnedZones];
        state.zonesList.forEach(zoneId => {
            const zone = state.zones[`${zoneId}`];
            if (zone) {
                if (!zone.zone_tags || zone.zone_tags.length === 0) {
                    if (state.zoneTagFilters.includes(NO_TAG_VALUE) && !filteredZones.includes(zoneId)) {
                        filteredZones.push(zoneId);
                    }
                } else {
                    const hasMatchingTag = zone.zone_tags.some(tag => state.zoneTagFilters.includes(tag));
                    if (hasMatchingTag && !filteredZones.includes(zoneId)) {
                        filteredZones.push(zoneId);
                    }
                }
            }
        });
        state.visibleZonesMap = filteredZones;
    } else if (state.zoneTagFilters && state.zoneTagFilters.length === 0) {
        state.visibleZonesMap = [...state.pinnedZones];
    } else {
        state.visibleZonesMap = state.zonesList;
    }
};

const sortZoneList = (state) => {
    state.zonesList = sortIdsByName(
        state.zonesList,
        zoneId => state.zones[`${zoneId}`]?.zone_name
    );
};

const sortMarkerList = (state) => {
    state.markersList = sortIdsByName(
        state.markersList,
        markerId => state.markers[`${markerId}`]?.marker_name
    );
};

const applyMarkerTagFiltersToMap = (state) => {
    if (state.markerTagFilters && state.markerTagFilters.length > 0 && state.markerTagFilters.length < tagList.length + 1) { // +1 for NO_TAG_VALUE
        let filteredMarkers = [...state.pinnedMarkers];
        state.markersList.forEach(markerId => {
            const marker = state.markers[`${markerId}`];
            if (marker) {
                if (!marker.marker_tags || marker.marker_tags.length === 0) {
                    if (state.markerTagFilters.includes(NO_TAG_VALUE) && !filteredMarkers.includes(markerId)) {
                        filteredMarkers.push(markerId);
                    }
                } else {
                    const hasMatchingTag = marker.marker_tags.some(tag => state.markerTagFilters.includes(tag));
                    if (hasMatchingTag && !filteredMarkers.includes(markerId)) {
                        filteredMarkers.push(markerId);
                    }
                }
            }
        });
        state.visibleMarkersMap = filteredMarkers;
    } else if (state.markerTagFilters && state.markerTagFilters.length === 0) {
        state.visibleMarkersMap = [...state.pinnedMarkers];
    } else {
        state.visibleMarkersMap = state.markersList;
    }
};

const updateZonePagination = (state) => {
    state.zonePageCount = Math.ceil(state.zonesList.length / state.itemsPerPage);
    if (state.zonePageCount > 0 && state.zonePageIndex > state.zonePageCount) {
        state.zonePageIndex = state.zonePageCount;
    }
    if (state.zonePageCount === 0) {
        state.zonePageIndex = 1;
    }
};

const updateMarkerPagination = (state) => {
    state.markerPageCount = Math.ceil(state.markersList.length / state.itemsPerPage);
    if (state.markerPageCount > 0 && state.markerPageIndex > state.markerPageCount) {
        state.markerPageIndex = state.markerPageCount;
    }
    if (state.markerPageCount === 0) {
        state.markerPageIndex = 1;
    }
};

const applyZoneDerivedState = (state) => {
    updateZonePagination(state);
    const search = (state.zoneSearchQuery || "").trim();
    const selectedTag = state.selectedtag || "";
    const hasSearch = search.length > 0;
    const hasSelectedTag = selectedTag.length > 0;
    let filteredList = null;
    if (hasSearch) {
        filteredList = buildZonesBySearch(state, search);
    } else if (hasSelectedTag) {
        filteredList = buildZonesBySelectedTag(state, selectedTag);
    }
    if (hasSearch || hasSelectedTag) {
        state.visibleZonesList = filteredList;
        state.visibleZonesMap = filteredList;
        return;
    }

    const startIndex = state.zonePageIndex === 1 ? 0 : (state.zonePageIndex - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage - 1;
    state.visibleZonesList = state.zonesList.slice(startIndex, endIndex + 1);

    const isDefaultTagFilter = state.zoneTagFilters && state.zoneTagFilters.length === tagList.length + 1;
    if (isDefaultTagFilter && !state.zonesAutoLoad) {
        state.visibleZonesMap = [...state.pinnedZones];
    } else {
        applyZoneTagFiltersToMap(state);
    }
};

const applyMarkerDerivedState = (state) => {
    updateMarkerPagination(state);
    const search = (state.markerSearchQuery || "").trim();
    const selectedTag = state.selectedtag || "";
    const hasSearch = search.length > 0;
    const hasSelectedTag = selectedTag.length > 0;
    let filteredList = null;
    if (hasSearch) {
        filteredList = buildMarkersBySearch(state, search);
    } else if (hasSelectedTag) {
        filteredList = buildMarkersBySelectedTag(state, selectedTag);
    }
    if (hasSearch || hasSelectedTag) {
        state.visibleMarkersList = filteredList;
        state.visibleMarkersMap = filteredList;
        return;
    }

    const startIndex = state.markerPageIndex === 1 ? 0 : (state.markerPageIndex - 1) * state.itemsPerPage;
    const endIndex = startIndex + state.itemsPerPage - 1;
    state.visibleMarkersList = state.markersList.slice(startIndex, endIndex + 1);

    const isDefaultTagFilter = state.markerTagFilters && state.markerTagFilters.length === tagList.length + 1;
    if (isDefaultTagFilter && !state.markersAutoLoad) {
        state.visibleMarkersMap = [...state.pinnedMarkers];
    } else {
        applyMarkerTagFiltersToMap(state);
    }
};

const upsertZoneState = (state, zoneData) => {
    const normalized = normalizeZoneData(zoneData);
    if (!normalized) {
        return;
    }
    state.zones[`${normalized.zone_id}`] = normalized;
    if (state.zonesList.indexOf(normalized.zone_id) === -1) {
        state.zonesList.push(normalized.zone_id);
    }
    sortZoneList(state);
    state.zoneEditData = {};
    applyZoneDerivedState(state);
};

const upsertMarkerState = (state, markerData) => {
    const normalized = normalizeMarkerData(markerData);
    if (!normalized) {
        return;
    }
    state.markers[`${normalized.marker_id}`] = normalized;
    if (state.markersList.indexOf(normalized.marker_id) === -1) {
        state.markersList.push(normalized.marker_id);
    }
    sortMarkerList(state);
    state.markerEditData = {};
    applyMarkerDerivedState(state);
};

const removeZoneState = (state, zoneId) => {
    if (zoneId === undefined || zoneId === null) {
        return;
    }
    delete state.zones[`${zoneId}`];
    state.zonesList = state.zonesList.filter(a => a !== zoneId);
    sortZoneList(state);
    state.pinnedZones = state.pinnedZones.filter(a => a !== zoneId);
    if (state.zoneEditData && state.zoneEditData.zone_id === zoneId) {
        state.zoneEditData = {};
    }
    if (state.zoneDetailView === zoneId) {
        state.zoneDetailView = null;
    }
    applyZoneDerivedState(state);
};

const removeMarkerState = (state, markerId) => {
    if (markerId === undefined || markerId === null) {
        return;
    }
    delete state.markers[`${markerId}`];
    state.markersList = state.markersList.filter(a => a !== markerId);
    sortMarkerList(state);
    state.pinnedMarkers = state.pinnedMarkers.filter(a => a !== markerId);
    if (state.markerEditData && state.markerEditData.marker_id === markerId) {
        state.markerEditData = {};
    }
    if (state.markerDetailView === markerId) {
        state.markerDetailView = null;
    }
    applyMarkerDerivedState(state);
};

export const placesSlice = createSlice({
    name: 'places',
    initialState: {
        isLoading: false,
        loaded: false,
        markers: {},
        zones: {},
        zonesList: [],
        markersList: [],
        visibleZonesList: [],
        visibleMarkersList: [],
        visibleZonesMap: [],
        visibleMarkersMap: [],
        pinnedMarkers: [],
        pinnedZones: [],
        zoneDetailView: null,
        markerDetailView: null,
        selectedtag: '',
        zoneSearchQuery: '',
        markerSearchQuery: '',
        markersAutoLoad: false,
        zonesAutoLoad: false,
        zoneDraftMapControl: false,
        zoneDraftCoords: [],
        zoneDraftColor: "#CF3827",
        zoneEditData: {},
        markerDraftMapControl: false,
        markerDraftCoords: {},
        markerDraftColor: "#00008B",
        markerEditData: {},
        itemsPerPage: 25,
        zonePageIndex: 1,
        zonePageCount: null,
        markerPageIndex: 1,
        markerPageCount: null,
        zoneTagFilters: [...tagList, NO_TAG_VALUE],
        markerTagFilters: [...tagList, NO_TAG_VALUE],
    },
    reducers: {
        addToPinnedMarker: (state, { payload }) => {
            state.pinnedMarkers = state.pinnedMarkers.indexOf(payload) === -1
                ? [...state.pinnedMarkers, payload]
                : state.pinnedMarkers;
            if (state.visibleMarkersMap.indexOf(payload) === -1) {
                state.visibleMarkersMap.push(payload);
            }
            return state;
        },
        removeFromPinnedMarker: (state, { payload }) => {
            state.pinnedMarkers = state.pinnedMarkers.indexOf(payload) !== -1
                ? state.pinnedMarkers.filter(a => a !== payload)
                : state.pinnedMarkers;
            return state;
        },
        addToPinnedZone: (state, { payload }) => {
            state.pinnedZones = state.pinnedZones.indexOf(payload) === -1
                ? [...state.pinnedZones, payload]
                : state.pinnedZones;
            if (state.visibleZonesMap.indexOf(payload) === -1) {
                state.visibleZonesMap.push(payload);
            }
            return state;
        },
        removeFromPinnedZone: (state, { payload }) => {
            state.pinnedZones = state.pinnedZones.indexOf(payload) !== -1
                ? state.pinnedZones.filter(a => a !== payload)
                : state.pinnedZones;
            return state;
        },
        unpinAllMarkers: (state) => {
            state.pinnedMarkers = [];
            return state;
        },
        unpinAllZones: (state) => {
            state.pinnedZones = [];
            return state;
        },
        addTovisibleMarkersList: (state, { payload }) => {
            state.visibleMarkersList = state.visibleMarkersList.indexOf(payload) === -1
                ? [...state.visibleMarkersList, payload]
                : state.visibleMarkersList;
            return state;
        },
        removeFromvisibleMarkersList: (state, { payload }) => {
            state.visibleMarkersList = state.visibleMarkersList.indexOf(payload) !== -1
                ? state.visibleMarkersList.filter(a => a !== payload)
                : state.visibleMarkersList;
            return state;
        },
        addTovisibleZonesList: (state, { payload }) => {
            state.visibleZonesList = state.visibleZonesList.indexOf(payload) === -1
                ? [...state.visibleZonesList, payload]
                : state.visibleZonesList;
            return state;
        },
        removeFromvisibleZonesList: (state, { payload }) => {
            state.visibleZonesList = state.visibleZonesList.indexOf(payload) !== -1
                ? state.visibleZonesList.filter(a => a !== payload)
                : state.visibleZonesList;
            return state;
        },
        addToVisibleMarkersMap: (state, { payload }) => {
            if (state.visibleMarkersMap.indexOf(payload) === -1) {
                state.visibleMarkersMap.push(payload);
            }
            return state;
        },
        removeFromVisibleMarkersMap: (state, { payload }) => {
            state.visibleMarkersMap = state.visibleMarkersMap.filter(a => a !== payload);
        },
        addToVisibleZonesMap: (state, { payload }) => {
            if (state.visibleZonesMap.indexOf(payload) === -1) {
                state.visibleZonesMap.push(payload);
            }
            return state;
        },
        removeFromVisibleZonesMap: (state, { payload }) => {
            state.visibleZonesMap = state.visibleZonesMap.filter(a => a !== payload);
        },
        clearVisibleMarkersMap: (state) => {
            state.visibleMarkersMap = [...state.pinnedMarkers];
            return state;
        },
        clearVisibleZonesMap: (state) => {
            console.log("Clearing zones", state.visibleZonesList.length);
            state.visibleZonesMap = [...state.pinnedZones];
            console.log("Cleared zones", state.visibleZonesMap.length);
            return state;
        },
        restoreVisibleMarkersMap: (state) => {
            // Use the tag filters if available
            if (state.markerTagFilters && state.markerTagFilters.length > 0 && state.markerTagFilters.length < tagList.length + 1) { // +1 for NO_TAG_VALUE
                // Apply tag filtering
                let filteredMarkers = [...state.pinnedMarkers]; // Always include pinned markers

                state.markersList.forEach(markerId => {
                    const marker = state.markers[`${markerId}`];
                    if (marker) {
                        // Handle markers with no tags
                        if (!marker.marker_tags || marker.marker_tags.length === 0) {
                            if (state.markerTagFilters.includes(NO_TAG_VALUE) && !filteredMarkers.includes(markerId)) {
                                filteredMarkers.push(markerId);
                            }
                        } else {
                            // Check if any of the marker's tags match the selected filters
                            const hasMatchingTag = marker.marker_tags.some(tag => state.markerTagFilters.includes(tag));
                            if (hasMatchingTag && !filteredMarkers.includes(markerId)) {
                                filteredMarkers.push(markerId);
                            }
                        }
                    }
                });
                state.visibleMarkersMap = filteredMarkers;
            } else if (state.markerTagFilters && state.markerTagFilters.length === 0) {
                // No tags selected, only show pinned markers
                state.visibleMarkersMap = [...state.pinnedMarkers];
            } else {
                // All tags selected or no filtering applied, show all markers
                state.visibleMarkersMap = state.markersList;
            }
            return state;
        },
        restoreVisibleZonesMap: (state) => {
            console.log("Restoring zones", state.visibleZonesList.length);

            // Use the tag filters if available
            if (state.zoneTagFilters && state.zoneTagFilters.length > 0 && state.zoneTagFilters.length < tagList.length + 1) { // +1 for NO_TAG_VALUE
                // Apply tag filtering
                let filteredZones = [...state.pinnedZones]; // Always include pinned zones

                state.zonesList.forEach(zoneId => {
                    const zone = state.zones[`${zoneId}`];
                    if (zone) {
                        // Handle zones with no tags
                        if (!zone.zone_tags || zone.zone_tags.length === 0) {
                            if (state.zoneTagFilters.includes(NO_TAG_VALUE) && !filteredZones.includes(zoneId)) {
                                filteredZones.push(zoneId);
                            }
                        } else {
                            // Check if any of the zone's tags match the selected filters
                            const hasMatchingTag = zone.zone_tags.some(tag => state.zoneTagFilters.includes(tag));
                            if (hasMatchingTag && !filteredZones.includes(zoneId)) {
                                filteredZones.push(zoneId);
                            }
                        }
                    }
                });
                state.visibleZonesMap = filteredZones;
            } else if (state.zoneTagFilters && state.zoneTagFilters.length === 0) {
                // No tags selected, only show pinned zones
                state.visibleZonesMap = [...state.pinnedZones];
            } else {
                // All tags selected or no filtering applied, show all zones
                state.visibleZonesMap = state.zonesList;
            }

            console.log("Restored zones", state.visibleZonesMap.length);
            return state;
        },
        searchZones: (state, { payload }) => {
            state.zoneSearchQuery = payload ?? "";
            applyZoneDerivedState(state);
            state.zoneDetailView = null;
            return state;
        },
        searchMarkers: (state, { payload }) => {
            state.markerSearchQuery = payload ?? "";
            applyMarkerDerivedState(state);
            state.markerDetailView = null;
            return state;
        },
        setZoneDetailView: (state, { payload }) => {
            state.zoneDetailView = payload;
            if (state.visibleZonesMap.indexOf(payload) === -1) {
                state.visibleZonesMap.push(payload);
            }
            return state;
        },
        removeZoneDetailView: (state) => {
            state.zoneDetailView = null;
            return state;
        },
        setMarkerDetailView: (state, { payload }) => {
            state.markerDetailView = payload;
            if (state.visibleMarkersMap.indexOf(payload) === -1) {
                state.visibleMarkersMap.push(payload);
            }
            return state;
        },
        removeMarkerDetailView: (state) => {
            state.markerDetailView = null;
            return state;
        },
        setSelectedTag: (state, { payload }) => {
            state.selectedtag = payload;
            state.zoneSearchQuery = "";
            state.markerSearchQuery = "";
            console.log("Tag selected ", payload);
            applyMarkerDerivedState(state);
            applyZoneDerivedState(state);
            state.markerDetailView = null;
            state.zoneDetailView = null;

            return state;
        },
        toggleZoneDrafting: (state) => {
            state.zoneDraftMapControl = !state.zoneDraftMapControl;
        },
        addZoneDraftCoords: (state, { payload }) => {
            state.zoneDraftCoords.push(payload);
            return state;
        },
        removeZoneDraftCoords: (state, { payload }) => {
            state.zoneDraftCoords.splice(payload, 1);
            return state;
        },
        clearZoneDraftCoords: (state, { payload }) => {
            state.zoneDraftCoords = [];
            return state;
        },
        relocateZoneDraftCoords: (state, { payload }) => {
            state.zoneDraftCoords[payload.index] = payload.coords;
            return state;
        },
        updateZoneDraftColor: (state, { payload }) => {
            state.zoneDraftColor = payload;
            return state;
        },
        editZone: (state, { payload }) => {
            state.zoneEditData = {
                zone_id: payload.zone_id,
                zone_name: payload.zone_name,
                zone_tags: payload.zone_tags,
                zone_color: payload.zone_color,
                zone_coords: payload.zone_coords,
            };
            return state;
        },
        updateSocketZone: (state, { payload }) => {
            upsertZoneState(state, payload);
            return state;
        },
        deleteSocketZone: (state, { payload }) => {
            const zoneId = payload?.zone_id ?? payload?.zoneId ?? payload?.id ?? payload;
            removeZoneState(state, zoneId);
            return state;
        },
        toggleMarkerDrafting: (state) => {
            state.markerDraftMapControl = !state.markerDraftMapControl;
        },
        addMarkerDraftCoords: (state, { payload }) => {
            state.markerDraftCoords = payload;
            return state;
        },
        removeMarkerDraftCoords: (state) => {
            state.markerDraftCoords = {};
            return state;
        },
        relocateMarkerDraftCoords: (state, { payload }) => {
            state.markerDraftCoords = payload;
            return state;
        },
        updateMarkerDraftColor: (state, { payload }) => {
            state.markerDraftColor = payload;
            return state;
        },
        editMarker: (state, { payload }) => {
            state.markerEditData = {
                marker_id: payload.marker_id,
                marker_name: payload.marker_name,
                marker_tags: payload.marker_tags,
                marker_coords: payload.marker_coords,
                marker_color: payload.marker_color,
                marker_desc: payload.marker_desc,
            };
            return state;
        },
        updateSocketMarker: (state, { payload }) => {
            upsertMarkerState(state, payload);
            return state;
        },
        deleteSocketMarker: (state, { payload }) => {
            const markerId = payload?.marker_id ?? payload?.markerId ?? payload?.id ?? payload;
            removeMarkerState(state, markerId);
            return state;
        },
        setItemsPerPage: (state, { payload }) => {
            state.itemsPerPage = payload;
            const startIndex = 0;
            const endIndex = startIndex + state.itemsPerPage - 1;
            state.zonePageIndex = 1;
            state.markerPageIndex = 1;
            state.zonePageCount = Math.ceil(state.zonesList.length / state.itemsPerPage);
            state.markerPageCount = Math.ceil(state.markersList.length / state.itemsPerPage);
            state.visibleZonesList = state.zonesList.slice(startIndex, endIndex + 1);
            state.visibleZonesMap = state.zonesList.slice(startIndex, endIndex + 1);
            state.visibleMarkersList = state.markersList.slice(startIndex, endIndex + 1);
            state.visibleMarkersMap = state.markersList.slice(startIndex, endIndex + 1);
            return state;
        },
        incrementZonePageIndex: (state) => {
            if (state.zonePageIndex < state.zonePageCount) {
                state.zonePageIndex++;
                const startIndex = state.zonePageIndex === 1 ? 0 : (state.zonePageIndex - 1) * state.itemsPerPage;
                const endIndex = startIndex + state.itemsPerPage - 1;
                state.visibleZonesList = state.zonesList.slice(startIndex, endIndex + 1);
            }
            return state;
        },
        decrementZonePageIndex: (state) => {
            if (state.zonePageIndex > 1) {
                state.zonePageIndex--;
                const startIndex = state.zonePageIndex === 1 ? 0 : (state.zonePageIndex - 1) * state.itemsPerPage;
                const endIndex = startIndex + state.itemsPerPage - 1;
                state.visibleZonesList = state.zonesList.slice(startIndex, endIndex + 1);
            }
            return state;
        },
        incrementMarkerPageIndex: (state) => {
            if (state.markerPageIndex < state.markerPageCount) {
                state.markerPageIndex++;
                const startIndex = state.markerPageIndex === 1 ? 0 : (state.markerPageIndex - 1) * state.itemsPerPage;
                const endIndex = startIndex + state.itemsPerPage - 1;
                state.visibleMarkersList = state.markersList.slice(startIndex, endIndex + 1);
            }
            return state;
        },
        decrementMarkerPageIndex: (state) => {
            if (state.markerPageIndex > 1) {
                state.markerPageIndex--;
                const startIndex = state.markerPageIndex === 1 ? 0 : (state.markerPageIndex - 1) * state.itemsPerPage;
                const endIndex = startIndex + state.itemsPerPage - 1;
                state.visibleMarkersList = state.markersList.slice(startIndex, endIndex + 1);
            }
            return state;
        },
        setZoneTagFilters: (state, { payload }) => {
            state.zoneTagFilters = payload;

            // Apply the tag filters to visible zones
            let filteredZones = [...state.pinnedZones]; // Always include pinned zones

            if (payload.length === 0) {
                // No tags selected, clear all non-pinned zones
                state.visibleZonesMap = filteredZones;
            } else if (payload.length === tagList.length + 1) { // +1 for NO_TAG_VALUE
                // All tags selected, show all zones
                state.visibleZonesMap = state.zonesList;
            } else {
                // Apply tag filtering
                state.zonesList.forEach(zoneId => {
                    const zone = state.zones[`${zoneId}`];
                    if (zone) {
                        // Handle zones with no tags
                        if (!zone.zone_tags || zone.zone_tags.length === 0) {
                            if (payload.includes(NO_TAG_VALUE) && !filteredZones.includes(zoneId)) {
                                filteredZones.push(zoneId);
                            }
                        } else {
                            // Check if any of the zone's tags match the selected filters
                            const hasMatchingTag = zone.zone_tags.some(tag => payload.includes(tag));
                            if (hasMatchingTag && !filteredZones.includes(zoneId)) {
                                filteredZones.push(zoneId);
                            }
                        }
                    }
                });
                state.visibleZonesMap = filteredZones;
            }

            return state;
        },
        setMarkerTagFilters: (state, { payload }) => {
            state.markerTagFilters = payload;

            // Apply the tag filters to visible markers
            let filteredMarkers = [...state.pinnedMarkers]; // Always include pinned markers

            if (payload.length === 0) {
                // No tags selected, clear all non-pinned markers
                state.visibleMarkersMap = filteredMarkers;
            } else if (payload.length === tagList.length + 1) { // +1 for NO_TAG_VALUE
                // All tags selected, show all markers
                state.visibleMarkersMap = state.markersList;
            } else {
                // Apply tag filtering
                state.markersList.forEach(markerId => {
                    const marker = state.markers[`${markerId}`];
                    if (marker) {
                        // Handle markers with no tags
                        if (!marker.marker_tags || marker.marker_tags.length === 0) {
                            if (payload.includes(NO_TAG_VALUE) && !filteredMarkers.includes(markerId)) {
                                filteredMarkers.push(markerId);
                            }
                        } else {
                            // Check if any of the marker's tags match the selected filters
                            const hasMatchingTag = marker.marker_tags.some(tag => payload.includes(tag));
                            if (hasMatchingTag && !filteredMarkers.includes(markerId)) {
                                filteredMarkers.push(markerId);
                            }
                        }
                    }
                });
                state.visibleMarkersMap = filteredMarkers;
            }

            return state;
        },
    },
    extraReducers: {
        [getPlaces.fulfilled]: (state, { payload }) => {
            const { markers, zones } = payload;
            let stateMarkers = {};
            let stateZones = {};
            let visibleMarkers = [];
            let visibleZones = [];
            if (markers) {
                if (markers.length > 0) {
                    markers.forEach(m => {
                        if (!isNaN(parseFloat(m.marker_coords.lat)) && !isNaN(parseFloat(m.marker_coords.lng))) {
                            visibleMarkers.push(m.marker_id);
                            stateMarkers = {
                                ...stateMarkers,
                                [`${m.marker_id}`]: {
                                    ...m,
                                    marker_coords: {
                                        lat: parseFloat(m.marker_coords.lat),
                                        lng: parseFloat(m.marker_coords.lng),
                                    }

                                },
                            };
                        }

                    })
                }
            }
            if (zones) {
                if (zones.length > 0) {
                    zones.forEach(m => {
                        let zc = true;
                        let coords = [];
                        m.zone_coords.forEach(z => {
                            if (!isNaN(parseFloat(z.lat)) && !isNaN(parseFloat(z.lng))) {
                                coords.push({ lat: parseFloat(z.lat), lng: parseFloat(z.lng) });
                            } else {
                                zc = false;
                            }
                        })
                        if (zc) {
                            visibleZones.push(m.zone_id);
                            stateZones = {
                                ...stateZones,
                                [`${m.zone_id}`]: {
                                    ...m,
                                    zone_coords: coords
                                },
                            };
                        }

                    })
                }
            }
            state.loaded = true;
            const sortedZones = sortIdsByName(
                visibleZones,
                zoneId => stateZones[`${zoneId}`]?.zone_name
            );
            const sortedMarkers = sortIdsByName(
                visibleMarkers,
                markerId => stateMarkers[`${markerId}`]?.marker_name
            );
            state.zonesList = sortedZones;
            state.markersList = sortedMarkers;
            state.markers = stateMarkers;
            state.zones = stateZones;
            if (sortedMarkers.length < maxMarkersAutoLoad) {
                state.visibleMarkersMap = sortedMarkers;
                state.markersAutoLoad = true;
            }
            if (sortedZones.length < maxZonesAutoLoad) {
                state.visibleZonesMap = sortedZones;
                state.zonesAutoLoad = true;
            } else {
            }
            state.visibleMarkersList = sortedMarkers.slice(0, state.itemsPerPage + 1);
            state.visibleZonesList = sortedZones.slice(0, state.itemsPerPage + 1);
            state.zonePageCount = Math.ceil(sortedZones.length / state.itemsPerPage);
            state.markerPageCount = Math.ceil(sortedMarkers.length / state.itemsPerPage);
            return state;
        },
        [saveZone.fulfilled]: (state, { payload }) => {
            console.log("Zone Saved", payload);
            const { zoneData } = payload;
            if (zoneData) {
                console.log("found zone data", zoneData);
                upsertZoneState(state, zoneData);
                console.log("Zone Updated", zoneData.zone_id, state.zones[zoneData.zone_id]);
            }
            return state;

        },
        [saveMarker.fulfilled]: (state, { payload }) => {
            console.log("MArker Saved", payload);
            const { markerData } = payload;
            if (markerData) {
                console.log("found marker data", markerData);
                upsertMarkerState(state, markerData);
                console.log("marker Updated", markerData.marker_id, state.markers[markerData.marker_id]);
            }
            return state;
        },
        [deleteZone.fulfilled]: (state, { payload }) => {
            const zoneId = payload?.zone_id ?? payload?.zoneId ?? payload?.id ?? payload;
            removeZoneState(state, zoneId);
        },
        [deleteMarker.fulfilled]: (state, { payload }) => {
            const markerId = payload?.marker_id ?? payload?.markerId ?? payload?.id ?? payload;
            removeMarkerState(state, markerId);
        }
    }
})

export { getPlaces, saveZone, saveMarker, deleteZone, deleteMarker };
export const {
    addToPinnedMarker, removeFromPinnedMarker, addToPinnedZone, removeFromPinnedZone,
    unpinAllMarkers, unpinAllZones, addTovisibleMarkersList, removeFromvisibleMarkersList,
    addTovisibleZonesList, removeFromvisibleZonesList, addToVisibleMarkersMap, removeFromVisibleMarkersMap,
    addToVisibleZonesMap, removeFromVisibleZonesMap, clearVisibleMarkersMap, clearVisibleZonesMap,
    restoreVisibleMarkersMap, restoreVisibleZonesMap, searchZones, searchMarkers,
    setZoneDetailView, removeZoneDetailView, setMarkerDetailView, removeMarkerDetailView, setSelectedTag,
    toggleZoneDrafting, addZoneDraftCoords, removeZoneDraftCoords, relocateZoneDraftCoords,
    updateZoneDraftColor, clearZoneDraftCoords, editZone, updateSocketZone, deleteSocketZone,
    toggleMarkerDrafting, addMarkerDraftCoords, removeMarkerDraftCoords, relocateMarkerDraftCoords,
    updateMarkerDraftColor, clearMarkerDraftCoords, editMarker, updateSocketMarker, deleteSocketMarker,
    setItemsPerPage, incrementZonePageIndex, decrementZonePageIndex,
    incrementMarkerPageIndex, decrementMarkerPageIndex, setZoneTagFilters, setMarkerTagFilters,
} = placesSlice.actions;

export const markersSelector = state => state.places.markers;
export const zonesSelector = state => state.places.zones;
export const markersListSelector = state => state.places.markersList;
export const zonesListSelector = state => state.places.zonesList;
export const pinnedMarkersListSelector = state => state.places.pinnedMarkers;
export const pinnedZonesListSelector = state => state.places.pinnedZones;
export const visibleMarkersListSelector = state => state.places.visibleMarkersList;
export const visibleZonesListSelector = state => state.places.visibleZonesList;
export const visibleMarkersMapSelector = state => state.places.visibleMarkersMap;
export const visibleZonesMapSelector = state => state.places.visibleZonesMap;
export const zoneDetailViewSelector = state => state.places.zoneDetailView;
export const markerDetailViewSelector = state => state.places.markerDetailView;
export const selectedtagSelector = state => state.places.selectedtag;
export const markerAutoLoadSelector = state => state.places.markersAutoLoad;
export const zoneAutoLoadSelector = state => state.places.zonesAutoLoad;
export const zoneDraftMapControlSelector = state => state.places.zoneDraftMapControl;
export const zoneDraftCoordsSelector = state => state.places.zoneDraftCoords;
export const zoneDraftColorSelector = state => state.places.zoneDraftColor;
export const zoneEditDataSelector = state => state.places.zoneEditData;
export const markerDraftMapControlSelector = state => state.places.markerDraftMapControl;
export const markerDraftCoordsSelector = state => state.places.markerDraftCoords;
export const markerDraftColorSelector = state => state.places.markerDraftColor;
export const markerEditDataSelector = state => state.places.markerEditData;
export const itemsPerPageSelector = state => state.places.itemsPerPage;
export const zonePageIndexSelector = state => state.places.zonePageIndex;
export const zonePageCountSelector = state => state.places.zonePageCount;
export const markerPageIndexSelector = state => state.places.markerPageIndex;
export const markerPageCountSelector = state => state.places.markerPageCount;
export const zoneTagFiltersSelector = state => state.places.zoneTagFilters;
export const markerTagFiltersSelector = state => state.places.markerTagFilters;
